import {
  OrderModify,
  RequestAccidents,
  StaffOrderAccidents
} from '@insurance/api/requests'
import {
  blobToBase64,
  deviceSession,
  formatDateToISOFormat
} from '@shared/utils'
import { NamePath } from 'antd/es/form/interface'
import dayjs from 'dayjs'
import { TFunction } from 'i18next'

import { AccidentFormValues, ContractTypeCode } from './type'
import { getContractorFields, getCustomerFields } from '../PersonsFields'

export const INSURANCE_FORM_DATA_KEY = '__accident_insurance_form_data__'
export const MIN_AGE_ACCIDENT_INSURANCE = 1
export const MIN_AGE = 18

export const saveAccidentValuesToStorage = (
  values: AccidentFormValues,
  id: string | number = ''
) => {
  const { prevStepKey } = formProps()
  const stringifyValues = JSON.stringify({
    ...values,
    [prevStepKey.name]: undefined
  })

  sessionStorage.setItem(id + INSURANCE_FORM_DATA_KEY, stringifyValues)
}

export const getAccidentValuesFromStorage = (id: string | number = '') => {
  const values = sessionStorage.getItem(id + INSURANCE_FORM_DATA_KEY)

  if (!values) return

  const parsedValues = JSON.parse(values)
  const { validFromProps, personsProps, effectiveDateProps } = formProps()

  return {
    ...parsedValues,
    [validFromProps.name]: dayjs(parsedValues[validFromProps.name]),
    [personsProps.name]: parsedValues[personsProps.name]?.map(
      (person: AccidentFormValues['persons'][number]) => ({
        ...person,
        ...(person?.date_of_birth && {
          date_of_birth: dayjs(person.date_of_birth)
        })
      })
    ),
    [effectiveDateProps.name]: parsedValues[effectiveDateProps.name]
      ? dayjs(parsedValues[effectiveDateProps.name])
      : undefined
  }
}

export const removeAccidentValuesFromStorage = (id: string | number = '') =>
  sessionStorage.removeItem(id + INSURANCE_FORM_DATA_KEY)

export const formProps = (tFunction?: TFunction) => {
  const t = tFunction ? tFunction : () => ''

  return {
    contractTypeProps: {
      name: 'contract_type',
      label: t('contract_type')
    },
    personsCountProps: {
      name: 'persons_count',
      label: t('persons_count')
    },
    durationProps: {
      name: 'duration',
      label: t('duration')
    },
    insuredAmountProps: {
      name: 'amount',
      label: t('insured_amount')
    },
    professionalRiskProps: {
      name: 'profession',
      label: t('professional_risk_level')
    },
    risksCoveredProps: {
      name: 'risks',
      label: t('main_insured_risks_covered')
    },
    ancillaryRisksCoveredProps: {
      name: 'ancillary_risks',
      label: t('ancillary_insured_risks_covered')
    },
    sportRiskProps: {
      name: 'sport',
      label: t('sport_risk_level')
    },
    territoryProps: {
      name: 'territory',
      label: t('coverage_type')
    },
    validFromProps: {
      name: 'valid_from',
      label: t('beginning_insurance_policy')
    },
    contractorProps: {
      name: 'contractor'
    },
    personsProps: {
      name: 'persons'
    },
    halfDayCoverageProps: {
      name: 'half_day_coverage',
      label: t('hours_coverage')
    },
    effectiveDateProps: {
      name: 'effective_date',
      label: t('effective_date')
    },
    creditProps: {
      name: 'credit',
      label: t('amount_used_from_balance')
    },

    // hidden fields
    stepKey: {
      name: 'step'
    },
    prevStepKey: {
      name: 'prev_step'
    },
    priceKey: {
      name: 'price'
    },
    sameContractorKey: {
      name: 'same_contractor_as_first_person'
    },
    risksKey: {
      name: '__risks'
    },
    personsStr: {
      name: 'persons_str'
    },
    prevPersonsCountKey: {
      name: '__prev_persons_count'
    },
    prevContractTypeKey: {
      name: '__prev_contract_type'
    },
    refundDestinationKey: {
      name: 'refund_destination'
    }
  } satisfies Record<string, { name: NamePath; label?: React.ReactNode }>
}

export const labelPersonsContractType = (
  t: TFunction,
  persons?: string | number
) => ({
  [ContractTypeCode.INDIVIDUAL]: null,
  [ContractTypeCode.FAMILIAL]: t(`family_${persons}_persons`),
  [ContractTypeCode.COLLECTIVE]: t(`collective_${persons}_persons`)
})

export const normalizeCommonData = async (
  values: AccidentFormValues,
  checkPersons?: boolean
) => {
  const session = deviceSession()

  const personsDocument = values.persons_str?.fileList?.[0]?.originFileObj
  const personsStr =
    personsDocument instanceof Blob ? await blobToBase64(personsDocument) : null

  return {
    valid_from: formatDateToISOFormat(values.valid_from)!,
    risks: [...values.risks, ...(values.ancillary_risks || [])],
    amount: values.amount,
    contract_type: values.contract_type,
    duration: values.duration,
    persons_count: values.persons_count,
    sport: values.sport,
    profession: values.profession,
    territory: values.territory,
    half_day_coverage: values.half_day_coverage,
    ...(checkPersons && {
      ...(personsStr
        ? { persons_str: personsStr.pureBase64 }
        : {
            persons: values?.persons?.map((person) => ({
              first_name: person.first_name || '',
              last_name: person.last_name || '',
              identification_number: person.identification_number || '',
              date_of_birth: formatDateToISOFormat(person.date_of_birth)!
            }))
          }),

      contractor: getContractorFields(values, { forceResident: true })
    }),

    session: {
      device_id: session.deviceId,
      session_id: session.sessionId
    }
  } as RequestAccidents
}

export const normalizeOrderData = async (values: AccidentFormValues) => {
  const prevValues = await normalizeCommonData(values, true)

  return {
    ...prevValues,
    customer: getCustomerFields(values.customer),
    credit: values.credit
  } as StaffOrderAccidents
}

export const normalizeModifyOrderData = async (
  values: AccidentFormValues
): Promise<OrderModify> => {
  const data = await normalizeOrderData(values)

  return {
    accidents: {
      persons: data.persons,
      effective_date: formatDateToISOFormat(values.effective_date)!,
      persons_str: data.persons_str,
      refund_destination: values.refund_destination,
      customer: data.customer
    }
  }
}

export const personsRestrictions = (
  contractType?: ContractTypeCode,
  persons?: number,
  currentPersons?: number
) => {
  if (!persons || !contractType || !currentPersons)
    return {
      restriction: { min: 0, max: 0 },
      addShow: false,
      deleteShow: false
    }

  const personsRange: Record<
    ContractTypeCode,
    Record<number, { min: number; max: number }>
  > = {
    [ContractTypeCode.INDIVIDUAL]: { 1: { min: 1, max: 1 } },
    [ContractTypeCode.FAMILIAL]: {
      2: { min: 2, max: 2 },
      3: { min: 3, max: 3 },
      4: { min: 4, max: 4 },
      5: { min: 5, max: Infinity }
    },
    [ContractTypeCode.COLLECTIVE]: {
      2: { min: 2, max: 10 },
      11: { min: 11, max: 50 },
      51: { min: 51, max: 100 },
      101: { min: 101, max: 500 },
      501: { min: 501, max: Infinity }
    }
  }

  const restriction = personsRange[contractType][persons]

  const personRange = Object.entries(personsRange[contractType]).find(
    ([_, { min, max }]) => currentPersons >= min && currentPersons <= max
  )?.[0]

  return {
    personRange: personRange ? Number(personRange) : undefined,
    restriction,
    addShow: currentPersons < restriction?.max,
    deleteShow: currentPersons > restriction?.min
  }
}
